// SWR-based API hooks for authentication
import { useCallback } from "react";
import useSWR from "swr";
import { AuthService } from "../services/auth";
// Create auth service instance
const authService = new AuthService();
/**
 * Hook to get current user data
 */
export function useCurrentUser() {
    const token = authService.getToken();
    const { data, error, isLoading, mutate } = useSWR(token ? ["auth/me", token] : null, async ([, token]) => authService.getCurrentUser(token), {
        revalidateOnFocus: false,
        shouldRetryOnError: false,
        onError: (error) => {
            // If token is invalid, remove it
            if (error.message.includes("Invalid") || error.message.includes("expired")) {
                authService.removeToken();
            }
        },
    });
    const refreshUser = useCallback(async () => {
        await mutate();
    }, [mutate]);
    return {
        user: data?.user || null,
        session: data?.session || null,
        isLoading,
        error: error?.message || null,
        refreshUser,
    };
}
/**
 * Hook for login functionality
 */
export function useLogin() {
    const login = useCallback(async (phoneNumber) => {
        return await authService.sendOTP(phoneNumber);
    }, []);
    return { login };
}
/**
 * Hook for OTP verification
 */
export function useVerifyOTP() {
    const verifyOTP = useCallback(async (phoneNumber, code) => {
        const response = await authService.verifyOTP(phoneNumber, code);
        // Store the session token
        authService.storeToken(response.sessionToken);
        return response;
    }, []);
    return { verifyOTP };
}
/**
 * Hook for logout functionality
 */
export function useLogout() {
    const logout = useCallback(async () => {
        const token = authService.getToken();
        if (token) {
            try {
                await authService.logout(token);
            }
            catch (error) {
                // Even if logout fails on server, remove local token
                console.error("Logout error:", error);
            }
        }
        // Always remove local token
        authService.removeToken();
        // Reload page to clear all state
        window.location.reload();
    }, []);
    return { logout };
}
